import type { DocNode } from '@atlaskit/adf-schema';

/**
 * @fileoverview ADF parsing utilities for handling guideline content
 *
 * TODO: Consider extracting these utilities to a shared ADF library once the API is stable.
 * This would benefit other teams dealing with similar ReactRenderer empty content issues.
 */

/**
 * Valid empty ADF document that ComposableEditor can work with
 */
const EMPTY_ADF_DOCUMENT: DocNode = {
	version: 1,
	type: 'doc',
	content: [],
};

/**
 * Type definitions for ADF content nodes we need to inspect
 */
type TextNode = {
	text?: string;
	type: 'text';
};

type ParagraphNode = {
	content?: Array<TextNode | unknown>;
	type: 'paragraph';
};

/**
 * Type guard to check if a parsed object is a valid ADF DocNode
 *
 * Performs basic structural validation of ADF documents. This intentionally does not validate
 * the entire ADF content tree, as ReactRenderer will handle that validation during rendering.
 * This approach allows for graceful degradation - if the ADF is invalid at a deeper level,
 * the component will fallback to plain text rendering.
 *
 * @param {unknown} obj - Object to validate
 * @returns {boolean} True if the object has the basic structure of an ADF DocNode
 */
const isValidADFDocument = (obj: unknown): obj is DocNode => {
	if (!obj || typeof obj !== 'object') {
		return false;
	}

	const doc = obj as Partial<DocNode>;

	// ADF documents must have type 'doc', version 1, and content array
	// Note: We don't validate the content array items themselves, as ReactRenderer
	// will handle that and provide appropriate error handling
	return doc.type === 'doc' && doc.version === 1 && Array.isArray(doc.content);
};

/**
 * Checks if a text node is empty (no text or only whitespace)
 */
const isEmptyText = (node: unknown): boolean => {
	if (!node || typeof node !== 'object') {
		return true;
	}

	const textNode = node as Partial<TextNode>;
	if (textNode.type !== 'text') {
		return false;
	}

	const text = textNode.text;
	return !text || text.trim().length === 0;
};

/**
 * Checks if a paragraph node is empty (no content or only empty text nodes)
 */
const isEmptyParagraph = (node: unknown): boolean => {
	if (!node || typeof node !== 'object') {
		return true;
	}

	const paragraph = node as Partial<ParagraphNode>;
	if (paragraph.type !== 'paragraph') {
		return false;
	}

	// Paragraph with no content property or empty array
	if (!('content' in paragraph) || !paragraph.content || paragraph.content.length === 0) {
		return true;
	}

	// Paragraph with only empty text nodes
	return paragraph.content.every(isEmptyText);
};

/**
 * Returns true if the given ADF doc is "visually empty".
 *
 * The ReactRenderer shows "Unsupported text" errors when it encounters certain
 * empty content structures that the backend may return (e.g., paragraphs with
 * empty text nodes). This function detects those cases so we can normalize them
 * to a proper empty ADF document, allowing components to gracefully fallback
 * to plain text rendering.
 *
 * Handles canonical empty forms like:
 * - {"version":1,"type":"doc","content":[]}
 * - {"version":1,"type":"doc","content":[{"type":"paragraph"}]}
 * - {"version":1,"type":"doc","content":[{"type":"paragraph","content":[]}]}
 * - {"version":1,"type":"doc","content":[{"type":"paragraph","content":[{"type":"text","text":""}]}]}
 * - And variations where text is only whitespace
 *
 * Note: Only handles single-paragraph empty documents to avoid false positives.
 * More complex documents are passed through for ReactRenderer to handle.
 *
 * @param {DocNode} doc - ADF document to check
 * @returns {boolean} True if the document is visually empty
 */
const isVisuallyEmptyAdf = (doc: DocNode): boolean => {
	// Type guard - should always be valid at this point
	if (!doc || doc.type !== 'doc') {
		return true;
	}

	const content = doc.content ?? [];

	// Empty content array
	if (content.length === 0) {
		return true;
	}

	// Only handle simple single-paragraph case to avoid false positives
	// More complex documents should be handled by ReactRenderer
	if (content.length === 1) {
		return isEmptyParagraph(content[0]);
	}

	return false;
};

/**
 * Safely parses guideline ADF JSON string with validation
 *
 * Returns valid empty ADF if:
 * - guideline is missing
 * - JSON parse fails
 * - object is not a valid ADF DocNode
 * - ADF is "visually empty" (would cause ReactRenderer to show errors)
 *
 * Always returns a valid DocNode. When content is invalid or visually empty,
 * returns EMPTY_ADF_DOCUMENT so that ReactRenderer can handle it gracefully
 * and components can fallback to plain text rendering by checking content.length.
 *
 * @param {string | undefined} guideline - JSON string to parse
 * @returns {DocNode} Parsed and validated ADF document or empty ADF document
 */
export const safeParseGuidelineADF = (guideline?: string): DocNode => {
	if (!guideline) {
		return EMPTY_ADF_DOCUMENT;
	}

	try {
		const parsed = JSON.parse(guideline);

		if (!isValidADFDocument(parsed)) {
			return EMPTY_ADF_DOCUMENT;
		}

		// Check if the ADF is visually empty (would cause renderer errors)
		if (isVisuallyEmptyAdf(parsed)) {
			return EMPTY_ADF_DOCUMENT;
		}

		return parsed;
	} catch {
		return EMPTY_ADF_DOCUMENT;
	}
};
